import { FC, useEffect, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { type Transcoder, USER_ROLE } from '@img-arena/consoles-api-streaming'
import { DataCard } from '@/components/card'
import { OpenDialogButton } from '@/components/ConsoleDialog/handlers'
import TranscoderForm from '@/components/forms/TranscoderForm'
import RoleDependent from '@/components/RoleDependent'
import {
  ActionDeleteTranscoderItem,
  ActionsMenu,
  ActionToggleTranscoderStateItem,
  ActionToggleTranscoderStatusItem
} from '@/components/table/actions'
import { FilterableTable } from '@/components/table/base'
import { CheckboxDeleteSelectedTranscoderItem, CheckboxMenu } from '@/components/table/checkbox'
import { TitleSetter } from '@/components/Title'
import { STORE_STATUS } from '@/constants/store'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { streamingRestApi } from '@/service/streamingApi'
import { useTranscodersStore } from '@/stores/transcoders'

import { getTranscodersTableFilters, TRANSCODERS_TABLE_COLUMNS } from './transcoders.table'

const tableId = 'dge-transcoders-table'
const POLLING_INTERVAL = 30 * 1000 // 30 seconds

const TranscodersView: FC = () => {
  const { data, isFetching, isLoading } = useTranscodersStore(
    useShallow((state) => ({
      data: state.data,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching
    }))
  )

  const { fetchData, resetState } = useTranscodersStore(
    useShallow((state) => ({
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, POLLING_INTERVAL)
    return () => {
      clearInterval(interval)
      resetState()
    }
  }, [])

  const handleDeleteTranscoders = createBulkHandler({
    name: 'Delete Transcoders',
    operation: async (transcoder: Transcoder) => {
      await streamingRestApi.endpoints.transcoders.deleteTranscoders(String(transcoder.id))
    },
    refreshData: fetchData,
    getItemDisplayName: (transcoder) => `ID: ${transcoder.id}`
  })

  const handleTranscoderStateToggle = async (transcoder: Transcoder) => {
    if (transcoder.disabled) {
      await streamingRestApi.endpoints.transcoders.enableTranscoder(transcoder.id)
    } else {
      await streamingRestApi.endpoints.transcoders.disableTranscoder(transcoder.id)
    }
    await fetchData()
  }

  const handleTranscoderStatusToggle = async (transcoder: Transcoder) => {
    if (transcoder.reserved) {
      await streamingRestApi.endpoints.transcoders.unreserveTranscoder(transcoder.id)
    } else {
      await streamingRestApi.endpoints.transcoders.reserveTranscoder(transcoder.id)
    }
    await fetchData()
  }

  const tableFilters = useMemo(() => getTranscodersTableFilters(data), [data])

  return (
    <>
      <TitleSetter title="Transcoders" />
      <DataCard
        title="Transcoders"
        headerPostfixActions={
          <>
            <CheckboxMenu tableId={tableId}>
              <CheckboxDeleteSelectedTranscoderItem onDelete={handleDeleteTranscoders} />
            </CheckboxMenu>
            <RoleDependent roles={[USER_ROLE.SUPER_ADMIN]}>
              <OpenDialogButton label="Add Transcoder">
                <TranscoderForm onSubmit={fetchData} />
              </OpenDialogButton>
            </RoleDependent>
          </>
        }
      >
        <FilterableTable
          data={data}
          tableId={tableId}
          loading={isLoading}
          fetching={isFetching}
          filters={tableFilters}
          columns={TRANSCODERS_TABLE_COLUMNS}
          clientSideSortAndPagination
          clientSideFilters
          noDataMessage="No active transcoders"
          withCheckboxes
          rowActions={
            <ActionsMenu>
              <ActionToggleTranscoderStateItem onClick={handleTranscoderStateToggle} />
              <ActionToggleTranscoderStatusItem onClick={handleTranscoderStatusToggle} />
              <ActionDeleteTranscoderItem
                onDelete={(row: Transcoder) => handleDeleteTranscoders([row])}
              />
            </ActionsMenu>
          }
        />
      </DataCard>
    </>
  )
}

export default TranscodersView
